import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './AdminTable.css';
import './AdminRevenue.css';

function AdminRevenue() {
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchRevenue();
  }, [user, navigate, period]);

  const fetchRevenue = async () => {
    try {
      setLoading(true);
      const params = { period };
      const response = await api.get('/api/admin/revenue', { params });
      setRevenueData(response.data);
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.revenue.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading">{t('admin.revenue.loading')}</div>
      </div>
    );
  }

  if (!revenueData) {
    return (
      <div className="admin-page">
        <div className="error">{t('admin.revenue.failedToLoad')}</div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-revenue">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.revenue.title')}</h1>
          <p>{t('admin.revenue.subtitle')}</p>
        </div>
      </div>

      <div className="filters">
        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="all">{t('admin.revenue.allTime')}</option>
          <option value="week">{t('admin.revenue.lastSevenDays')}</option>
          <option value="month">{t('admin.revenue.thisMonth')}</option>
          <option value="year">{t('admin.revenue.thisYear')}</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="revenue-summary">
        <div className="summary-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <div className="card-label">{t('admin.revenue.totalRevenue')}</div>
            <div className="card-value">{formatCurrency(revenueData.summary.total_revenue)}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">📅</div>
          <div className="card-content">
            <div className="card-label">{t('admin.revenue.totalBookings')}</div>
            <div className="card-value">{revenueData.summary.total_bookings}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <div className="card-label">{t('admin.revenue.averageBookingValue')}</div>
            <div className="card-value">{formatCurrency(revenueData.summary.average_booking_value)}</div>
          </div>
        </div>
      </div>

      {/* Revenue by Property Type */}
      <div className="revenue-section">
        <h2>{t('admin.revenue.revenueByType')}</h2>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>{t('admin.revenue.propertyType')}</th>
                <th>{t('admin.revenue.revenue')}</th>
                <th>{t('admin.revenue.bookings')}</th>
                <th>{t('admin.revenue.avgPerBooking')}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(revenueData.revenue_by_type || {}).map(([type, data]) => (
                <tr key={type}>
                  <td className="capitalize">{type}</td>
                  <td className="revenue-value">{formatCurrency(data.revenue)}</td>
                  <td>{data.bookings}</td>
                  <td>{formatCurrency(data.revenue / data.bookings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {Object.keys(revenueData.revenue_by_type || {}).length === 0 && (
            <div className="no-results">{t('admin.revenue.noDataAvailable')}</div>
          )}
        </div>
      </div>

      {/* Top Earning Properties */}
      <div className="revenue-section">
        <h2>{t('admin.revenue.topEarningProperties')}</h2>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>{t('admin.revenue.property')}</th>
                <th>{t('admin.revenue.host')}</th>
                <th>{t('admin.revenue.revenue')}</th>
                <th>{t('admin.revenue.bookings')}</th>
              </tr>
            </thead>
            <tbody>
              {(revenueData.top_properties || []).map((prop, index) => (
                <tr key={prop.property_id}>
                  <td>
                    <div className="property-cell">
                      <span className="rank">#{index + 1}</span>
                      <div>
                        <div>{prop.property_title}</div>
                        <div className="sub-text">{prop.property_city}</div>
                      </div>
                    </div>
                  </td>
                  <td>{prop.host_name}</td>
                  <td className="revenue-value">{formatCurrency(prop.revenue)}</td>
                  <td>{prop.bookings}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(revenueData.top_properties || []).length === 0 && (
            <div className="no-results">{t('admin.revenue.noPropertiesFound')}</div>
          )}
        </div>
      </div>

      {/* Top Earning Hosts */}
      <div className="revenue-section">
        <h2>{t('admin.revenue.topEarningHosts')}</h2>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>{t('admin.revenue.host')}</th>
                <th>{t('admin.revenue.propertiesCount')}</th>
                <th>{t('admin.revenue.totalRevenue')}</th>
              </tr>
            </thead>
            <tbody>
              {(revenueData.top_hosts || []).map((host, index) => (
                <tr key={host.host_id}>
                  <td>
                    <div className="host-cell">
                      <span className="rank">#{index + 1}</span>
                      <div>
                        <div>{host.host_name}</div>
                        <div className="sub-text">{host.host_email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{host.properties_count}</td>
                  <td className="revenue-value">{formatCurrency(host.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(revenueData.top_hosts || []).length === 0 && (
            <div className="no-results">{t('admin.revenue.noHostsFound')}</div>
          )}
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="revenue-section">
        <h2>{t('admin.revenue.revenueTrend')}</h2>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>{t('admin.revenue.month')}</th>
                <th>{t('admin.revenue.revenue')}</th>
                <th>{t('admin.revenue.bookings')}</th>
              </tr>
            </thead>
            <tbody>
              {(revenueData.monthly_trend || []).map((month) => (
                <tr key={month.month}>
                  <td>{month.month}</td>
                  <td className="revenue-value">{formatCurrency(month.revenue)}</td>
                  <td>{month.bookings}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(revenueData.monthly_trend || []).length === 0 && (
            <div className="no-results">{t('admin.revenue.noTrendData')}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminRevenue;
